import { insertWordBreaks, convertStylesToStyled, formatBytes, getLineageMetaTitle } from '@/_utils/utils';
import { selectInventorySelections, selectVersionKeys } from '@/redux/selectors/orderPlacementSelectors';
import { selectTitleId } from '@/redux/selectors/titleSelectors';
import { addInventorySelection, removeInventorySelection } from '@/redux/slices/orderPlacementSlice';
import styled from '@emotion/styled';
import { Box, Grid, Tooltip, Typography, Palette, darken } from '@mui/material';
import React, { useContext, useMemo } from 'react';
import { InventoryFileEntity } from '@/redux/slices/slice_types';
import { selectFileTypes } from '@/redux/selectors/constantsSelectors';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { formatDate } from '@/_utils/date';
import { Theme } from '@emotion/react';
import InventoryFileOverflowMenu from './InventoryFileOverflowMenu';
import PolarisIcon from '../PolarisIcon';
import FileInventoryTags from '@/components/TitleDetail/TitleDetailInventory/FileInventoryTags';
import { ConfigContext } from '@/contextAPI/configContext';
import { selectActiveTitleId } from '@/redux/selectors/titleV2Selectors';
import { useConfigValue } from '@/components/_hooks/useConfigValue';

interface OPInventoryFileProps {
  file: InventoryFileEntity;
  onRemove?: () => void;
  onClick?: () => void;
  afterFileSelection?: () => void;
  disableDownloads: boolean;
  disablePreviews?: boolean;
  disableSelected: boolean;
  isReview?: boolean;
  canDelete?: boolean;
  previewOnly?: boolean;
  xOnly?: boolean;
  noOverflowMenu?: boolean;
  showTitleLabel?: boolean;
}

const InventoryFile = ({
  file,
  onRemove,
  onClick,
  afterFileSelection,
  disableDownloads = false,
  disablePreviews = false,
  disableSelected = false,
  isReview = false,
  canDelete,
  previewOnly = false,
  xOnly = false,
  noOverflowMenu = false,
  showTitleLabel = false,
}: OPInventoryFileProps) => {
  const isTitleV2 = useContext(ConfigContext)?.titleConfig?.v2;
  const dispatch = useAppDispatch();
  const fileSize = formatBytes(file.file_size);
  const id = useAppSelector(isTitleV2 ? selectActiveTitleId : selectTitleId);
  const inventorySelections = useAppSelector(selectInventorySelections);
  const fileTypes = useAppSelector(selectFileTypes);
  const isSelected = inventorySelections.find(({ id }: InventoryFileEntity) => id === file.id);
  const fileType = file.file_category || fileTypes[file.file_type] || 'other';
  const versionKeys = useAppSelector(selectVersionKeys);
  const enableTitleLabel = useConfigValue(['orderPlacementConfig', 'showFileTitleLabel']);

  const addFileSelection = () => {
    if (isSelected) {
      dispatch(removeInventorySelection(file));
    } else {
      if (afterFileSelection) afterFileSelection();
      dispatch(addInventorySelection({ ...file, parent_id: id }));
    }
  };

  const lastModifiedDate = useMemo(() => {
    if (!file.instances || !Object.keys(file.instances).find((inst) => file.instances[inst].lastModified)) return undefined;
    const lastModifiedKey = Object.keys(file.instances).reduce((latestKey, currentKey) => {
      return file.instances[currentKey]?.lastModified > file.instances[latestKey]?.lastModified ? currentKey : latestKey;
    }, Object.keys(file.instances)[0]);
    return lastModifiedKey ? file.instances[lastModifiedKey].lastModified : lastModifiedKey;
  }, [file]);

  const fileNameNoPath = useMemo(() => file.name?.split('/').pop() || '', [file]);
  const shouldShowExtraIcon = ['future', 'archive', 'missing'].includes(file.status) && !(!isReview && onRemove);

  return (
    <>
      {enableTitleLabel && showTitleLabel && <TitleLabel>
        {getLineageMetaTitle(versionKeys[file?.version_id])}
      </TitleLabel>}
      <InventoryFileDiv
        data-testid={`inventory-file-${file.id}`}
        isSelected={(isSelected && !disableSelected) || false}
        selectableFile={!disableSelected}
        onClick={onClick ? onClick : (onRemove || disableSelected ? undefined : addFileSelection)}
        className={`${isSelected ? 'isSelected' : ''}`}
        style={{ cursor: onRemove || disableSelected ? 'initial' : 'pointer' }}
        title={+(enableTitleLabel && showTitleLabel)}
      >
        <ColoredBar type={fileType} extra={+shouldShowExtraIcon}>
          <div />
        </ColoredBar>
        <StyledGrid
          container
          justifyContent="space-between"
          alignItems="center"
          flexWrap="nowrap"
          previewonly={+previewOnly}
          sx={!noOverflowMenu ? { maxWidth: 'calc(100% - 53px)' } : undefined}
        >
          <Grid item sx={{ maxWidth: 'calc(100% - 75px)', flexGrow: 1 }}>
            <Grid container flexDirection="column">
              <Grid container alignItems="center" direction={'row'}>
                <Typography sx={{ fontSize: '1.1rem', fontWeight: 'bold' }} noWrap>
                  {insertWordBreaks(fileNameNoPath)}
                  {shouldShowExtraIcon && (
                    <Tooltip title={file.status === 'future' ? 'Future' : file.status === 'missing' ? 'Missing' : 'Archived'}>
                      <StyledArchiveIcon height={18} icon={file.status === 'future' || file.status === 'missing' ? 'clock_future' : 'archive'} />
                    </Tooltip>
                  )}
                </Typography>
              </Grid>
              <Box sx={{ display: 'block', width: '100%', mt: 0.5 }}>
                <FileInventoryTags file={file} showFutureLabel={file.status === 'future' && !isReview && !!onRemove} />
              </Box>
            </Grid>
          </Grid>
          <Grid container flexDirection="column" sx={{ width: 'unset' }} alignItems="flex-start">
            {!!file.file_size && (
              <Tooltip title="File Size" arrow placement="left">
                <Grid container alignItems="center" sx={{ flexWrap: 'nowrap' }}>
                  <PolarisIcon icon="lucide:hard-drive" height={18} style={{ opacity: 0.4 }} />
                  <Typography noWrap variant="caption" sx={{ fontSize: '.82rem', ml: 1 }}>
                    {fileSize}
                  </Typography>
                </Grid>
              </Tooltip>
            )}
            {lastModifiedDate && (
              <Tooltip title="Last modified" arrow placement="left">
                <Grid container alignItems="center" sx={{ flexWrap: 'nowrap', mt: 0.75 }}>
                  <PolarisIcon icon="eos-icons:modified-date-outlined" height={18} style={{ opacity: 0.4 }} />
                  <Typography noWrap variant="caption" sx={{ fontSize: '.82rem', ml: 1 }}>
                    {formatDate(new Date(lastModifiedDate).toISOString())}
                  </Typography>
                </Grid>
              </Tooltip>
            )}
          </Grid>
        </StyledGrid>
        {!noOverflowMenu && (
          <InventoryFileOverflowMenu
            file={file}
            onRemove={onRemove}
            disableDownloads={disableDownloads}
            disablePreviews={disablePreviews}
            canDelete={canDelete}
            previewOnly={previewOnly}
            xOnly={xOnly}
          />
        )}
      </InventoryFileDiv>
    </>

  );
};

export default React.memo(InventoryFile);

const InventoryFileDiv = styled.div<{
  isSelected: boolean;
  selectableFile: boolean;
  theme?: Theme;
  title: number;
}>`
  position: relative;
  border-radius: ${({ theme, title }) => (title ? `0 0 ${theme.shape.borderRadius}px ${theme.shape.borderRadius}px` : `${theme.shape.borderRadius}px`)};
  
  margin: ${({ theme }) => theme?.spacing(1, 0)};
  transition: ease background-color 0.2s;
  background: ${({ theme }) => theme?.palette.background.default + '6a'};
  overflow: hidden;
  padding-left: ${({ theme }) => theme?.spacing(2.5)};
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;

  ${({ theme }) => convertStylesToStyled(theme?.customOverrides?.InventoryFile)};

  ${({ theme, isSelected }) =>
    isSelected
      ? `
    background-color: ${theme?.palette.primary.main + '3a'};
  `
      : null};

  ${({ theme, selectableFile }) =>
    selectableFile
      ? `
    &:hover {
      transition: ease background-color .2s;
      background-color: ${theme?.palette.primary.main + '2a'};
    }
  `
      : null};
`;

const StyledArchiveIcon = styled(PolarisIcon)`
  opacity: 0.6;
  margin-left: ${({ theme }) => theme.spacing(1)};
  margin-bottom: ${({ theme }) => theme.spacing(-0.25)};
`;

const getTabColor = (type: string) => {
  switch (type) {
    case 'video':
      return 'primary';
    case 'audio':
      return 'secondary';
    case 'image':
      return 'tertiary';
    default:
      return 'default';
  }
};

const ColoredBar = styled.div<{ type: string; extra: number }>`
  width: 6px;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  z-index: 4;
  background: ${({ theme, type }) => theme.palette[getTabColor(type) as keyof Palette].main};

  > div {
    height: 100%;
    width: 6px;
    box-shadow: inset 0 -1px 0 rgba(0, 0, 0, 0.15);

    background-image: ${({ theme, type, extra }) => {
    const color = getTabColor(type);
    if (extra) {
      const baseColor = darken(theme.palette[color].main, 0.4);
      const lightColor = theme.palette[color].main;
      return `linear-gradient(45deg, ${baseColor} 25%, ${lightColor} 0, ${lightColor} 50%, ${baseColor} 0, ${baseColor} 75%, ${lightColor} 0, ${lightColor})`;
    } else {
      return theme.palette[color].main;
    }
  }};
    background-size: 10px 10px;
  }
`;

const StyledGrid = styled(Grid) <{ previewonly: number }>`
  padding: ${({ theme }) => theme?.spacing(1.5)};

  ${({ previewonly: previewOnly, theme }) =>
    previewOnly
      ? `
      border-right: 1px solid ${theme.palette.divider};
    `
      : null};
`;

const TitleLabel = styled.div`
  background: ${({ theme }) => theme.palette.primary.main};
  padding: ${({ theme }) => theme.spacing(0.5, 1, 2, 2)};
  font-weight: bold;
  border-radius: ${({ theme }) => theme.shape.borderRadius}px;
  margin-bottom: ${({ theme }) => theme.spacing(-2.5)};
`;